// src/billing/Result.jsx
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Stack, Typography, CircularProgress } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import axios from 'axios';
import { apiUrl } from 'api/http';
import { getToken as getAuthToken } from 'api/auth';

const Glow = ({ color }) => (
  <Box sx={{
    position:'absolute', inset:-40, zIndex:0,
    background:`radial-gradient(closest-side, ${color}22, transparent 70%)`,
    filter:'blur(6px)'
  }}/>
);

export default function BillingResult({ mode }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const token = getAuthToken({ admin: false });
  const qp = useMemo(() => new URLSearchParams(search), [search]);

  const [status, setStatus] = useState('loading'); // loading | success | fail
  const [msg, setMsg] = useState('Verifying your payment…');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (mode === 'cancel') {
          if (!alive) return;
          setStatus('fail');
          setMsg('Payment was cancelled.');
          return;
        }
        const sid = qp.get('session_id') || qp.get('sessionId') || qp.get('session');
        if (!sid) { setStatus('fail'); setMsg('Missing session id.'); return; }
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.post(apiUrl('api/subscriptions/verify-session/'), { session_id: sid }, { headers });
        if (!alive) return;
        localStorage.setItem('has_sub', '1');  // <— remember active subscription
        setStatus('success');
        setMsg('Subscription activated. Welcome aboard!');
        
      } catch (e) {
        if (!alive) return;
        setStatus('fail');
        setMsg(e?.response?.data?.detail || e.message || 'Verification failed.');
      }
    })();
    return () => { alive = false; };
  }, [mode, qp, token]);

  useEffect(() => {
    if (status === 'loading') return;
    const t = setTimeout(() => navigate('/dashboard'), 2200);
    return () => clearTimeout(t);
  }, [status, navigate]);

  const Icon = status === 'success' ? CheckCircleRoundedIcon :
               status === 'fail'    ? ErrorRoundedIcon :
               CircularProgress;

  const border = status === 'success' ? '#22c55e' : status === 'fail' ? '#ef4444' : '#0ea5e9';

  return (
    <Box sx={{ minHeight:'100dvh', display:'grid', placeItems:'center', p:3, bgcolor:'background.default' }}>
      <Paper elevation={8} sx={{
        p:{ xs:3, md:5 }, maxWidth:720, width:'100%', position:'relative',
        borderRadius:4, overflow:'hidden', textAlign:'center',
        border:`1px solid ${border}33`, background:'linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)'
      }}>
        <Glow color={border} />
        <Stack spacing={2} sx={{ position:'relative', zIndex:1 }}>
          <Icon sx={{ fontSize:64, color:border, mx:'auto' }} />
          <Typography variant="h4" fontWeight={800}>
            {status === 'success' ? 'Payment Successful' :
             status === 'fail'    ? 'Payment Incomplete' : 'Processing…'}
          </Typography>
          <Typography variant="body1" color="text.secondary">{msg}</Typography>
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ pt:1 }}>
            <Button variant="contained" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
            {status !== 'loading' && (
              <Button startIcon={<ReplayRoundedIcon />} onClick={() => window.location.reload()}>
                Refresh
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            You’ll be redirected automatically.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
